/**
 * editorUtils.js
 * Utility functions for the SRMD Video Edit Suite
 */

/**
 * Convert seconds to HH:MM:SS:FF timecode string.
 * @param {number} seconds
 * @param {number} fps - frames per second (default 25)
 */
export function secondsToTimecode(seconds, fps = 25) {
    if (!isFinite(seconds) || seconds < 0) return '00:00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const f = Math.floor((seconds % 1) * fps);
    return [h, m, s, f].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Convert HH:MM:SS.mmm (SRT/VTT format) to seconds.
 */
export function timecodeToSeconds(tc) {
    if (!tc) return 0;
    // Handle both comma and dot as decimal separator (SRT uses comma)
    const normalised = tc.replace(',', '.');
    const parts = normalised.split(':');
    if (parts.length === 3) {
        return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
    }
    if (parts.length === 2) {
        return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
    }
    return parseFloat(normalised);
}

/**
 * Convert seconds back to SRT timecode: HH:MM:SS,mmm
 */
export function secondsToSrtTimecode(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
}

/**
 * Parse an SRT string into an array of cue objects.
 */
export function parseSrt(srtText) {
    if (!srtText) return [];
    const blocks = srtText.trim().split(/\n\s*\n/);
    const cues = [];
    blocks.forEach((block, idx) => {
        const lines = block.trim().split('\n');
        if (lines.length < 2) return;
        // First line may be a sequence number
        let timeLineIdx = 0;
        if (/^\d+$/.test(lines[0].trim())) {
            timeLineIdx = 1;
        }
        const timeLine = lines[timeLineIdx];
        if (!timeLine) return;
        const match = timeLine.match(/(\S+)\s*-->\s*(\S+)/);
        if (!match) return;
        const text = lines.slice(timeLineIdx + 1).join('\n').trim();
        cues.push({
            id: `cue_${idx}`,
            startTime: timecodeToSeconds(match[1]),
            endTime: timecodeToSeconds(match[2]),
            text,
        });
    });
    return cues;
}

/**
 * Parse a VTT string into an array of cue objects.
 */
export function parseVtt(vttText) {
    if (!vttText) return [];
    const lines = vttText.split('\n');
    const cues = [];
    let i = 0;
    // Skip header
    while (i < lines.length && !lines[i].includes('-->')) i++;

    while (i < lines.length) {
        const line = lines[i].trim();
        if (line.includes('-->')) {
            const match = line.match(/(\S+)\s*-->\s*(\S+)/);
            if (match) {
                const textLines = [];
                i++;
                while (i < lines.length && lines[i].trim() !== '') {
                    textLines.push(lines[i].trim());
                    i++;
                }
                cues.push({
                    id: `vtt_${cues.length}`,
                    startTime: timecodeToSeconds(match[1]),
                    endTime: timecodeToSeconds(match[2]),
                    text: textLines.join('\n'),
                });
            }
        }
        i++;
    }
    return cues;
}

/**
 * Serialize cue array back to SRT string.
 */
export function cuesToSrt(cues) {
    return cues
        .map((cue, idx) => {
            return `${idx + 1}\n${secondsToSrtTimecode(cue.startTime)} --> ${secondsToSrtTimecode(cue.endTime)}\n${cue.text}`;
        })
        .join('\n\n');
}

/**
 * Build an SRT string with cue timestamps remapped to the output timeline after cuts.
 * Cues that fall entirely within a deleted segment are dropped.
 * Cues that straddle a cut boundary are clipped to the nearest kept boundary.
 */
export function cuesToSrtWithCuts(cues, cuts) {
    if (!cuts || cuts.length === 0) return cuesToSrt(cues);

    const sorted = [...cuts].sort((a, b) => a.startTime - b.startTime);

    const remapped = [];
    for (const cue of cues) {
        // Compute output times for start and end
        let outStart = originalToOutputTime(cue.startTime, sorted);
        let outEnd = originalToOutputTime(cue.endTime, sorted);

        // If start is inside a cut, move it to the cut's end (in output time)
        if (outStart === null) {
            // Find the cut containing cue.startTime
            const containingCut = sorted.find(c => cue.startTime >= c.startTime && cue.startTime < c.endTime);
            outStart = containingCut ? originalToOutputTime(containingCut.endTime, sorted) : null;
        }
        // If end is inside a cut, clip it to the cut's start (in output time).
        // We can't use originalToOutputTime(cut.startTime) because that returns null
        // (cut.startTime is inside the deleted region per >= condition).
        // Instead, compute output time directly: cut.startTime minus all prior cuts' durations.
        if (outEnd === null) {
            const containingCut = sorted.find(c => cue.endTime >= c.startTime && cue.endTime < c.endTime);
            if (containingCut) {
                let priorOffset = 0;
                for (const c of sorted) {
                    if (c === containingCut) break;
                    priorOffset += c.endTime - c.startTime;
                }
                outEnd = containingCut.startTime - priorOffset;
            }
        }

        if (outStart === null || outEnd === null || outEnd <= outStart) continue;

        remapped.push({ ...cue, startTime: outStart, endTime: outEnd });
    }

    return remapped
        .map((cue, idx) => `${idx + 1}\n${secondsToSrtTimecode(cue.startTime)} --> ${secondsToSrtTimecode(cue.endTime)}\n${cue.text}`)
        .join('\n\n');
}

/**
 * Given the full list of cuts (already applied sequentially),
 * compute the "output" position of an original-timeline position.
 * Returns null if the position falls inside a deleted segment.
 */
export function originalToOutputTime(originalTime, cuts) {
    let offset = 0;
    for (const cut of cuts) {
        if (originalTime >= cut.startTime && originalTime < cut.endTime) {
            return null; // inside a deleted region
        }
        if (originalTime >= cut.endTime) {
            offset += cut.endTime - cut.startTime;
        }
    }
    return originalTime - offset;
}

/**
 * Convert output timeline position back to original timeline position.
 */
export function outputToOriginalTime(outputTime, cuts) {
    let accumulated = 0;
    for (const cut of cuts) {
        const cutDuration = cut.endTime - cut.startTime;
        // position in output just before this cut's gap
        const cutOutputStart = cut.startTime - accumulated;
        if (outputTime < cutOutputStart) break;
        accumulated += cutDuration;
    }
    return outputTime + accumulated;
}

/**
 * Calculate total output duration after applying all cuts.
 */
export function getOutputDuration(originalDuration, cuts) {
    const totalCut = cuts.reduce((sum, c) => sum + (c.endTime - c.startTime), 0);
    return Math.max(0, originalDuration - totalCut);
}

/**
 * Get all segments of the ORIGINAL timeline that are NOT deleted.
 * Returns [{ startTime, endTime }] in original time.
 */
export function getKeptSegments(duration, cuts) {
    if (!cuts || cuts.length === 0) {
        return [{ startTime: 0, endTime: duration }];
    }
    const sorted = [...cuts].sort((a, b) => a.startTime - b.startTime);
    const kept = [];
    let cursor = 0;
    for (const cut of sorted) {
        if (cursor < cut.startTime) {
            kept.push({ startTime: cursor, endTime: cut.startTime });
        }
        cursor = cut.endTime;
    }
    if (cursor < duration) {
        kept.push({ startTime: cursor, endTime: duration });
    }
    return kept;
}

/**
 * Build the EDL (Edit Decision List) payload for the backend.
 * Each entry is a kept segment in original video time.
 */
export function buildEdl(duration, cuts) {
    return getKeptSegments(duration, cuts).map((seg, idx) => ({
        index: idx,
        sourceStart: seg.startTime,
        sourceEnd: seg.endTime,
    }));
}

/**
 * Get the segments between cut markers, for use in the UI.
 * Returns an ordered list of { startTime, endTime, isDeleted } segments.
 *
 * NOTE: When a segment is deleted, its boundary cut markers are removed from state.
 * To still divide the timeline at deleted-segment boundaries, we also include
 * each cut's startTime/endTime as division points.
 */
export function getTimelineSegments(duration, cutMarkers, cuts) {
    const cutBoundaries = cuts.flatMap((c) => [c.startTime, c.endTime]);
    const allPoints = [...new Set([0, ...cutMarkers, ...cutBoundaries, duration])]
        .sort((a, b) => a - b);
    const segments = [];
    for (let i = 0; i < allPoints.length - 1; i++) {
        const startTime = allPoints[i];
        const endTime = allPoints[i + 1];
        const isDeleted = cuts.some((c) => c.startTime <= startTime && c.endTime >= endTime);
        segments.push({ startTime, endTime, isDeleted });
    }
    return segments;
}
